import { useEffect, useRef, useState } from "react";
import { sfx } from "../../lib/sound";
import { useSound } from "../SoundToggle";
import { Timer } from "lucide-react";
import type { GroupState } from "./GroupParts";

export function useRemaining(deadline: string | null, serverNow: string) {
  const [offset] = useState(() => Date.parse(serverNow) - Date.now());
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!deadline) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [deadline]);
  if (!deadline) return null;
  return Math.max(0, Date.parse(deadline) - (now + offset));
}

export function Countdown({ group }: { group: GroupState }) {
  const ms = useRemaining(group.round_deadline, group.server_now);
  const sound = useSound();
  const secs = ms === null ? null : Math.ceil(ms / 1000);
  const prev = useRef<number | null>(null);
  useEffect(() => {
    if (sound && secs !== null && prev.current !== null && secs !== prev.current) {
      if (secs > 0 && secs <= 10) sfx.tick(secs <= 3, group.sound_style);
      if (secs === 0 && prev.current > 0) sfx.buzzer(group.sound_style);
    }
    prev.current = secs;
  }, [secs, sound, group.sound_style]);
  if (secs === null || group.status !== "voting") return null;
  const urgent = secs <= 10;
  const label = secs > 0 ? `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}` : "Time's up!";
  return (
    <div className={`flex items-center justify-between gap-3 rounded-2xl border-[3px] border-ink px-4 py-3 pop-shadow-sm ${urgent ? "bg-pop-pink text-white" : "bg-white"}`} data-testid="group-countdown">
      <span className="inline-flex items-center gap-2 text-xs font-extrabold uppercase tracking-widest"><Timer className="h-4 w-4" strokeWidth={3} />
        {group.mode === "best_of_3" ? `Round ${group.current_round} closes in` : "Voting closes in"}
      </span>
      <span className={`font-display text-3xl font-bold tabular-nums ${urgent && secs > 0 ? "animate-pulse" : ""}`} data-testid="group-countdown-value">{secs > 0 ? label : "Revealing…"}</span>
    </div>
  );
}

export const TIMER_CHOICES = [
  { s: 0, label: "No limit" }, { s: 30, label: "30s" }, { s: 60, label: "1 min" }, { s: 120, label: "2 min" }, { s: 300, label: "5 min" }, { s: 600, label: "10 min" },
];
